import { useEffect, useState } from 'react';
import { type KeyboardLayoutMap } from '../../../onboarding/keyboard/layoutDisplayLabel';

interface KeyboardWithLayoutMap {
  getLayoutMap(): Promise<Map<string, string>>;
}

const hasKeyboardLayoutMap = (
  nav: Navigator
): nav is Navigator & { keyboard: KeyboardWithLayoutMap } =>
  'keyboard' in nav &&
  nav.keyboard != null &&
  typeof (nav.keyboard as KeyboardWithLayoutMap).getLayoutMap === 'function';

/**
 * Loads the browser Keyboard Layout Map (`KeyboardEvent.code` → printed glyph).
 * Returns null until resolved or when the API is unavailable.
 */
export const useKeyboardLayoutMap = (): KeyboardLayoutMap | null => {
  const [layoutMap, setLayoutMap] = useState<KeyboardLayoutMap | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (!hasKeyboardLayoutMap(navigator)) return;
        const map = await navigator.keyboard.getLayoutMap();
        setLayoutMap(map);
      } catch {
        // Fallback to formatChordTokens output — API unavailable or permission denied
      }
    };

    void load();
  }, []);

  return layoutMap;
};
